import { memo, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { LoadingState } from '../shared/components/ui';
import { ConsoleDocumentRoutes } from './ConsoleDocumentRoutes';
import { findGroup } from './dockModel';
import { useDockElements, useWorkspace } from './WorkspaceProvider';
import { panelHostId, type WorkspaceState } from './workspaceState';

/*
 * Where the documents actually live.
 *
 * Every open document is mounted exactly once, here, and projected into whichever pane
 * or window owns it at the moment. Because its position in the React tree never changes,
 * dragging a tab across the workspace re-parents a DOM node and nothing more: the page
 * keeps its queries, its filters, and whatever half-finished form is on it.
 *
 * A document that is not in front stays mounted but is marked inert, so it neither
 * answers the keyboard nor reaches a screen reader while it waits behind another tab.
 */

function isInFront(state: WorkspaceState, panelId: string): boolean {
  const hostId = panelHostId(state, panelId);
  if (hostId === null) return false;
  const window = state.windows.find((candidate) => candidate.id === hostId);
  if (window !== undefined) return window.state !== 'minimized' && window.activePanelId === panelId;
  const group = findGroup(state.root, hostId);
  if (group === undefined || group.activePanelId !== panelId) return false;
  return state.maximizedGroupId === null || state.maximizedGroupId === hostId;
}

interface DocumentPoolProps {
  /**
   * The phone shell has one surface rather than a dock, so it lends every document the
   * same container and names the one document that should be on it.
   */
  readonly soloHost?: HTMLElement | null;
  readonly soloPanelId?: string | null;
}

export function DocumentPool({ soloHost, soloPanelId }: DocumentPoolProps = {}) {
  const state = useWorkspace();
  const { bodies } = useDockElements();
  const solo = soloHost != null;

  return (
    <>
      {Object.values(state.panels).map((panel) => {
        const hostId = panelHostId(state, panel.id);
        const host = solo ? soloHost : hostId === null ? undefined : bodies[hostId];
        if (host == null) return null;
        return createPortal(
          <DocumentSurface
            panelId={panel.id}
            address={panel.address}
            visible={solo ? soloPanelId === panel.id : isInFront(state, panel.id)}
          />,
          host,
          panel.id,
        );
      })}
    </>
  );
}

const DocumentSurface = memo(function DocumentSurface({ panelId, address, visible }: { panelId: string; address: string; visible: boolean }) {
  return (
    <div
      id={`dock-document-${panelId}`}
      role="tabpanel"
      className={`dock-document${visible ? '' : ' dock-document--behind'}`}
      inert={!visible}
    >
      <Suspense fallback={<LoadingState />}>
        <ConsoleDocumentRoutes address={address} />
      </Suspense>
    </div>
  );
});
